import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';

import AcceptanceTemplateForm from './AcceptanceTemplateForm';
import AcceptanceTemplatePreview from './AcceptanceTemplatePreview';
import RegistrationPayment from './RegistrationPayment/RegistrationPayment';
import ChatTab from './Chat/ChatTab';

import {
  encrypt,
  generatePreSignedUrl,
  getDataFromLocalStorage,
} from 'utils/helpers';
import { eventAcceptanceLetterPath, icons } from 'utils/constants';
import {
  generateAcceptanceTemplate,
  throwError,
  throwSuccess,
} from 'store/slices';
import axios from 'axios';
import { saveAs } from 'file-saver';

// ==========================
// Tabs Configuration
// ==========================
const tabsData = [
  { index: '0', eventKey: 'acceptance-form', title: 'Acceptance Form' },
  { index: '1', eventKey: 'payment', title: 'Payment' },
  { index: '2', eventKey: 'chats', title: 'Chats' },
];

// ==========================
// Component
// ==========================
const AcceptanceState = ({
  status,
  data,
  paperData,
  handleSuccess,
  setTabType,
  tabType,
}) => {
  const dispatch = useDispatch();
  const { userDetails } = useSelector((state) => ({
    userDetails: state.student.userDetails,
  }));

  const templatePreviewRef = useRef(null);
  const formRef = useRef();
  const chatRef = useRef(null);
  const paymentRef = useRef(null);
  const hasScrolled = useRef(false);

  const [btnLoader, setBtnLoader] = useState(false);

  // ==========================
  // Derived Variables
  // ==========================
  const { is_remodify, review_skipped } = data || {};

  const { submittedPapersStatus, payment_status, unread_chat_count } =
    paperData || {};

  const { acceptance_letter } = submittedPapersStatus || {};

  const paymentIsAccepted = ['1'].includes(payment_status);
  const IsAcceptanceCompleted = status >= 3 && acceptance_letter;
  const isActive = status >= 3;
  const isActiveState = is_remodify === '2';

  const prefillCoAuthors =
    paperData?.co_author_details?.map((o) => o?.name)?.join(', ') || '';
  const isLatestPaper = paperData?.latest_paper_title || '';

  const userType = getDataFromLocalStorage('user_type');

  // ==========================
  // Role Based Access
  // ==========================
  const { update: updateAccess, create: createAccess } =
    userDetails?.role_data?.find(
      (item) => item?.permission_name === 'Event Management'
    )?.sub_permission || {};

  const isEnable = updateAccess === '1' || createAccess === '1';

  // ==========================
  // Formik - Initial Values
  // ==========================
  const initialValues = {
    client_name: paperData?.authorName || '',
    paper_title: isLatestPaper || paperData?.paperTitle || '',
    conference_name: paperData?.conference || '',
    location: paperData?.event_location || '',
    from_date: paperData?.conference_date || '',
    to_date: paperData?.conference_to_date || '',
    session_name: paperData?.session_name || '',
    bref_description_of_session: paperData?.bref_description_of_session || '',
    sdg_number: paperData?.sdg_number || '',
    brief_description_of_sdg: paperData?.brief_description_of_sdg || '',
    registration_deadline: paperData?.registration_deadline || '',
    discount_amount: paperData?.discount_amount || '',
    coupon_code: paperData?.coupon_code || '',
    registration_link: '',
    your_name: '',
    contact_details: '',
    preferences: {
      whatsapp: '',
      email: '',
    },
  };

  // ==========================
  // Formik - Validation Schema
  // ==========================
  const validationSchema = Yup.object({
    client_name: Yup.string().required('Client name is required'),
    paper_title: Yup.string().required('Paper title is required'),
    conference_name: Yup.string().required('Conference name is required'),
    location: Yup.string().required('Location is required'),
    from_date: Yup.date().required('From Date is required').nullable(),
    to_date: Yup.date().required('To Date is required').nullable(),
    session_name: Yup.string().required('Session Name is required'),
    bref_description_of_session: Yup.string().required(
      'Brief Description Of Session is required'
    ),
    sdg_number: Yup.string().required('SDG Number is required'),
    brief_description_of_sdg: Yup.string().required(
      'Brief Description Of SDG is required'
    ),
    registration_deadline: Yup.date()
      .nullable()
      .required('Registration deadline is required')
      .min(new Date(new Date().setHours(0, 0, 0, 0)), 'Cannot be in the past')
      .test(
        'is-before-from-date',
        'Must be on or before the conference start date',
        function (value) {
          const { from_date } = this.parent;
          if (!value || !from_date) return true;
          return (
            new Date(value).setHours(0, 0, 0, 0) <=
            new Date(from_date).setHours(0, 0, 0, 0)
          );
        }
      ),
    discount_amount: Yup.string().required('Discount amount is required'),
    coupon_code: Yup.string().required('Coupon code is required'),
    registration_link: Yup.string()
      .required('Registration Link is required')
      .url(),
    your_name: Yup.string().required('Your Name is required'),
    contact_details: Yup.string().required('Contact Details is required'),
    preferences: Yup.object().test(
      'at-least-one-selected',
      'Select at least one contact preference',
      (values) => values.whatsapp || values.email
    ),
  });

  // ==========================
  // Form Submission
  // ==========================
  const handleSave = async (values) => {
    setBtnLoader(true);
    const htmlContent = templatePreviewRef.current?.innerHTML?.trim();

    const payload = {
      paper_submitted_status: '4',
      id: paperData?.id,
      acceptance_letter: encrypt(htmlContent),
      values: JSON.stringify(values),
    };

    const formData = new FormData();
    Object.entries(payload).forEach(([key, val]) => formData.append(key, val));
    Object.values(values.preferences).forEach((val) => {
      if (val) formData.append('send_via[]', val);
    });

    const response = await dispatch(generateAcceptanceTemplate(formData));

    if (response.status === 200) {
      formRef.current?.resetForm();
      dispatch(throwSuccess(response?.message));
      handleSuccess();
    } else {
      dispatch(throwError({ message: response?.message }));
    }

    setBtnLoader(false);
  };

  // ==========================
  // Scroll to Tab Logic
  // ==========================
  useEffect(() => {
    const scrollToRef = {
      2: chatRef,
      1: paymentRef,
    }[tabType];

    if (scrollToRef?.current && !hasScrolled.current) {
      scrollToRef.current.scrollIntoView({ behavior: 'smooth' });
      hasScrolled.current = true;
    }
  }, [tabType]);

  useEffect(() => {
    hasScrolled.current = false;
  }, [tabType]);

  // ==========================
  // Render
  // ==========================
  return (
    <>
      {/* Header */}
      <div className="d-flex align-items-center">
        <div
          className={`steps-block ${
            isActiveState || review_skipped === '1' ? 'active' : 'not-active'
          }`}
        >
          {IsAcceptanceCompleted ? (
            <img src={icons.RightIcon} alt="Completed" />
          ) : (
            '4'
          )}
        </div>
        <div
          className={`text-16-500-20 ${
            isActiveState || review_skipped === '1'
              ? 'color-new-car'
              : 'color-6866'
          }`}
        >
          Acceptance for the Conference
        </div>
      </div>

      {/* Tabs and Content */}
      <div className="d-flex">
        <div className="sparator-block">
          <span className="hr-line" />
        </div>
        <div className="cpt-14 w-100 acceptance-state-block">
          {isActiveState || review_skipped === '1' ? (
            <>
              {/* Tabs */}
              <div className="d-flex align-items-center gap-4 cmb-20">
                <Tabs
                  activeKey={tabType}
                  onSelect={(key) => setTabType(key)}
                  className="mb-3 gap-5"
                >
                  {tabsData.map((tab) => (
                    <Tab key={tab.index} eventKey={tab.index} title={tab.title}>
                      {tab.index === '2' && unread_chat_count > 0 && (
                        <span className="position-absolute unread-msg text-14-400">
                          {unread_chat_count}
                        </span>
                      )}
                    </Tab>
                  ))}
                </Tabs>
              </div>

              {/* Tab Content */}
              {isActive ? (
                paymentIsAccepted ? (
                  <div className="cpb-14 text-14-400-18 color-black-olive">
                    Payment successfully completed!!
                  </div>
                ) : (
                  <>
                    {tabType === '0' &&
                      (IsAcceptanceCompleted ? (
                        <>
                          <div className="cpb-14 text-14-400-18 color-black-olive">
                            Acceptance successfully completed!!
                          </div>
                          {userType === '6' && (
                            <div className="cmb-16 text-16-500-20 color-7cff d-flex gap-2 align-items-center">
                              <span
                                className={`${
                                  acceptance_letter ? 'pointer' : ''
                                } border-end cpe-10`}
                                onClick={async () => {
                                  let downloadContent = '';
                                  if (acceptance_letter) {
                                    downloadContent =
                                      await generatePreSignedUrl(
                                        acceptance_letter,
                                        eventAcceptanceLetterPath
                                      );
                                  }
                                  window.open(downloadContent, '_blank');
                                }}
                              >
                                View
                              </span>
                              <span
                                className={`${
                                  acceptance_letter ? 'pointer' : ''
                                }`}
                                onClick={async () => {
                                  let downloadContent = '';
                                  if (acceptance_letter) {
                                    downloadContent =
                                      await generatePreSignedUrl(
                                        acceptance_letter,
                                        eventAcceptanceLetterPath
                                      );
                                  }
                                  const response = await axios.get(
                                    downloadContent,
                                    {
                                      responseType: 'blob',
                                    }
                                  );
                                  const blob = new Blob([response?.data], {
                                    type: response?.data?.type,
                                  });
                                  saveAs(blob, 'AcceptanceLetter.pdf');
                                  // dispatch(downloadFile(downloadContent));
                                }}
                              >
                                Download Acceptance Letter
                              </span>
                            </div>
                          )}
                        </>
                      ) : (
                        <Formik
                          initialValues={initialValues}
                          validationSchema={validationSchema}
                          onSubmit={handleSave}
                          innerRef={formRef}
                        >
                          {(props) => (
                            <div className="row gy-4">
                              <div className="col-lg-6">
                                <AcceptanceTemplateForm
                                  props={props}
                                  initialValues={initialValues}
                                  btnLoader={btnLoader}
                                  paperData={paperData}
                                  isEnable={isEnable}
                                />
                              </div>
                              <div className="col-lg-6 bg-f6ff overflow-auto">
                                <div className="cpt-21 cpe-21 cpb-21 cps-21">
                                  <AcceptanceTemplatePreview
                                    values={props.values}
                                    templatePreviewRef={templatePreviewRef}
                                    paperData={paperData}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </Formik>
                      ))}
                    {tabType === '1' && (
                      <div ref={paymentRef} className="cmb-20">
                        <RegistrationPayment
                          paperData={paperData}
                          handleSuccess={handleSuccess}
                        />
                      </div>
                    )}
                    {tabType === '2' && (
                      <div ref={chatRef}>
                        <ChatTab paperData={paperData} />
                      </div>
                    )}
                  </>
                )
              ) : (
                tabType === '2' &&
                !paymentIsAccepted && (
                  <div ref={chatRef}>
                    <div className="text-16-500 cpb-10 color-new-car">Chat</div>
                    <ChatTab paperData={paperData} />
                  </div>
                )
              )}
            </>
          ) : (
            tabType === '2' &&
            !paymentIsAccepted && (
              <div className="cpt-20" ref={chatRef}>
                <div className="text-16-500 cpb-10 color-new-car">Chat</div>
                <ChatTab paperData={paperData} />
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
};

export default AcceptanceState;

// import React, { useEffect, useRef, useState } from 'react';
// import AcceptanceTemplatePreview from './AcceptanceTemplatePreview';
// import { Formik } from 'formik';
// import * as Yup from 'yup';
// import AcceptanceTemplateForm from './AcceptanceTemplateForm';
// import { encrypt } from 'utils/helpers';
// import { useDispatch, useSelector } from 'react-redux';
// import Tab from 'react-bootstrap/Tab';
// import Tabs from 'react-bootstrap/Tabs';
// import {
//   generateAcceptanceTemplate,
//   throwError,
//   throwSuccess,
// } from 'store/slices';
// import { icons } from 'utils/constants';
// import RegistrationPayment from './RegistrationPayment/RegistrationPayment';
// import ChatTab from './Chat/ChatTab';

// const AcceptanceState = ({
//   status,
//   data,
//   paperData,
//   handleSuccess,
//   setTabType,
//   tabType,
// }) => {
//   const { userDetails } = useSelector((state) => ({
//     userDetails: state.student.userDetails,
//   }));
//   const dispatch = useDispatch();
//   const templatePreviewRef = useRef(null);
//   const formRef = useRef();
//   const chatRef = useRef(null);
//   const paymentRef = useRef(null);
//   const [btnLoader, setBtnLoader] = useState(false);
//   const { is_remodify, review_skipped } = data || {};
//   const { submittedPapersStatus, payment_status } = paperData || {};
//   const paymentIsAccepted = ['1'].includes(payment_status);
//   const { acceptance_letter } = submittedPapersStatus || {};
//   const isActive = status >= 3;
//   const isActiveState = is_remodify === '2';
//   const IsAcceptanceCompeted = status >= 3 && acceptance_letter;
//   const prefillCoAuthors = paperData?.co_author_details
//     ? paperData?.co_author_details?.map((o) => o?.name)?.join(', ')
//     : '';
//   const isLatestPaper = paperData?.latest_paper_title
//     ? paperData?.latest_paper_title
//     : '';

//   const initialValues = {
//     client_name: paperData?.authorName || '',

//     paper_title: isLatestPaper
//       ? paperData?.latest_paper_title
//       : paperData?.paperTitle || '',
//     conference_name: paperData?.conference || '',
//     location: paperData?.event_location || '',
//     from_date: paperData?.conference_date || '',
//     to_date: paperData?.conference_to_date || '',
//     session_name: paperData?.session_name || '',
//     bref_description_of_session: paperData?.bref_description_of_session || '',
//     sdg_number: paperData?.sdg_number || '',
//     brief_description_of_sdg: paperData?.brief_description_of_sdg || '',
//     registration_deadline: paperData?.registration_deadline || '',
//     discount_amount: paperData?.discount_amount || '',
//     coupon_code: paperData?.coupon_code || '',
//     // your_organization_name: paperData?.your_organization_name || '',

//     // conference_organizer: paperData?.organizer_name || '',
//     // presentation_type: paperData?.presentation_type || '',
//     // conference_short_name: paperData?.conference_short_name || '',
//     // paper_id: paperData?.paperId || '',
//     // coAuthor_name: prefillCoAuthors || '',
//     // relevant_field: '',
//     // video_link: '',
//     // complete_registration: '',
//     // support_email: '',
//     // conference_theme: '',
//     // your_contact_information: '',
//     // your_website: '',
//     preferences: {
//       whatsapp: '',
//       // sms: "",
//       email: '',
//     },
//   };

//   const validationSchema = Yup.object({
//     client_name: Yup.string().required('Client name is required'),
//     paper_title: Yup.string().required('Paper title is required'),
//     conference_name: Yup.string().required('Conference name is required'),
//     location: Yup.string().required('Location is required'),
//     from_date: Yup.date().required('From Date is required').nullable(),
//     to_date: Yup.date().required('To Date is required').nullable(),

//     session_name: Yup.string().required('Session Name is required'),
//     bref_description_of_session: Yup.string().required(
//       'Brief Description Of Session is required'
//     ),
//     sdg_number: Yup.string()
//       // .matches(/^\d+$/, 'SDG Number must be a number')
//       .required('SDG Number is required'),
//     brief_description_of_sdg: Yup.string().required(
//       'Brief Description Of SDG is required'
//     ),
//     // registration_deadline: Yup.date()
//     //   .required('Registration Date is required')
//     //   .nullable(),

//     registration_deadline: Yup.date()
//       .nullable()
//       .required('Registration deadline is required')
//       .min(
//         new Date(new Date().setHours(0, 0, 0, 0)),
//         'Registration deadline cannot be in the past'
//       )
//       .test(
//         'is-before-from-date',
//         'Registration deadline must be on or before the selected from date',
//         function (value) {
//           const { from_date } = this.parent;
//           if (!value || !from_date) return true;

//           const regDate = new Date(value).setHours(0, 0, 0, 0);
//           const startDate = new Date(from_date).setHours(0, 0, 0, 0);

//           return regDate <= startDate;
//         }
//       ),
//     discount_amount: Yup.string().required('Discount amount is required'),
//     coupon_code: Yup.string().required('Coupon code is required'),
//     registration_link: Yup.string()
//       .required('Registration Link is required')
//       .url('Enter a valid URL'),
//     your_name: Yup.string().required('Your Name is required'),
//     contact_details: Yup.string().required('Contact Details is required'),

//     // your_organization_name: Yup.string().required(
//     //   'Organization name is required'
//     // ),
//     // conference_organizer: Yup.string().required(
//     //   'Conference organizer is required'
//     // ),
//     // presentation_type: Yup.string().required('Presentation type is required'),
//     // paper_id: Yup.string().required('Paper ID is required'),
//     // coAuthor_name: Yup.string(),

//     // relevant_field: Yup.string().required('Relevant field is required'),
//     // video_link: Yup.string().url('Enter a valid URL'),

//     // complete_registration: Yup.string().required('link is required'),
//     // support_email: Yup.string()
//     //   .email("Enter a valid email")
//     //   .required("Support email is required"),
//     // conference_theme: Yup.string().required('Conference theme is required'),
//     // your_contact_information: Yup.string().required(
//     //   'Contact information is required'
//     // ),
//     // your_website: Yup.string()
//     //   .url('Enter a valid URL')
//     //   .required('Website is required'),
//     preferences: Yup.object().test(
//       'at-least-one-selected',
//       'Please select at least one contact preference',
//       (values) => values.whatsapp || values.email
//       // values.sms ||
//     ),
//   });

//   const handleSave = async (values) => {
//     setBtnLoader(true);
//     if (templatePreviewRef.current) {
//       const htmlContent = templatePreviewRef.current.innerHTML.trim();

//       const payload = {
//         paper_submitted_status: '4',
//         id: paperData?.id,
//         acceptance_letter: encrypt(htmlContent),
//         values: JSON.stringify(values),
//       };

//       let formData = new FormData();
//       for (let key in payload) {
//         formData.append(key, payload[key]);
//       }
//       for (let key in values?.preferences) {
//         formData.append(`send_via[]`, values?.preferences[key]);
//       }

//       const response = await dispatch(generateAcceptanceTemplate(formData));
//       if (response.status === 200) {
//         if (formRef.current) {
//           formRef.current.resetForm();
//         }
//         dispatch(throwSuccess(response?.message));
//         handleSuccess();
//       } else {
//         dispatch(throwError({ message: response?.messsage }));
//       }

//       setBtnLoader(false);
//     }
//   };

//   const tabsData = [
//     {
//       index: '0',
//       eventKey: 'acceptance-form',
//       title: 'Acceptance Form',
//     },
//     {
//       index: '1',
//       eventKey: 'payment',
//       title: 'Payment',
//     },
//     {
//       index: '2',
//       eventKey: 'chats',
//       title: 'Chats',
//     },
//   ];
//   const hasScrolled = useRef(false);

//   useEffect(() => {
//     if (tabType === '2' && chatRef.current && !hasScrolled.current) {
//       chatRef.current.scrollIntoView({ behavior: 'smooth' });
//       hasScrolled.current = true;
//     } else if (tabType === '1' && paymentRef.current && !hasScrolled.current) {
//       paymentRef.current.scrollIntoView({ behavior: 'smooth' });
//       hasScrolled.current = true;
//     }
//   }, [tabType]);

//   // Reset scroll flag on every tab change so it scrolls every time tab is selected
//   useEffect(() => {
//     hasScrolled.current = false;
//   }, [tabType]);

//   const {
//     // view: viewAccess,
//     update: updateAccess,
//     // delete: deleteAccess,
//     create: createAccess,
//   } =
//     userDetails?.role_data?.find(
//       (item) => item?.permission_name === 'Event Management'
//     )?.sub_permission || {};
//   const isEnable = updateAccess === '1' || createAccess === '1';

//   return (
//     <>
//       <div className="d-flex justify-content-start align-items-center">
//         <div
//           className={`steps-block ${
//             (isActiveState || review_skipped === '1') && status >= 3
//               ? 'active'
//               : 'not-active'
//           }`}
//         >
//           {IsAcceptanceCompeted ? (
//             <img src={icons?.RightIcon} alt="right" />
//           ) : (
//             '4'
//           )}
//         </div>
//         <div>
//           <div
//             className={`text-16-500-20 ${
//               (isActiveState || review_skipped === '1') && status >= 3
//                 ? 'color-new-car'
//                 : 'color-6866'
//             }`}
//           >
//             Acceptance for the Conference
//           </div>
//         </div>
//       </div>
//       <div className="d-flex justify-content-start">
//         <div className="sparator-block">
//           <span className="hr-line" />
//         </div>
//         <div className="cpt-14 cpb-20 w-100 acceptance-state-block">
//           {isActiveState || review_skipped === '1' ? (
//             <div>
//               <div className="d-flex align-items-center gap-4 cmb-20">
//                 <Tabs
//                   activeKey={tabType} // Active tab based on `tabType`
//                   onSelect={(key) => setTabType(key)} // Update `tabType` when a tab is selected
//                   className="mb-3 gap-5"
//                 >
//                   {tabsData?.map((tab) => (
//                     <Tab
//                       key={tab.eventKey}
//                       eventKey={tab.index} // Associate tab with its `index`
//                       title={tab.title} // Tab title
//                     >
//                       {tab.index === '2' &&
//                         paperData?.unread_chat_count > 0 && (
//                           <span className="position-absolute unread-msg text-14-400">
//                             {paperData?.unread_chat_count || 0}
//                           </span>
//                         )}
//                       {/* Tab content goes here */}
//                     </Tab>
//                   ))}
//                 </Tabs>
//               </div>
//               {isActive ? (
//                 paymentIsAccepted ? (
//                   <div className="cpb-14 w-100 text-14-400-18 color-black-olive">
//                     Payment successfully completed !!
//                   </div>
//                 ) : (
//                   <>
//                     {tabType === '0' &&
//                       (IsAcceptanceCompeted ? (
//                         <div className="cpb-14 w-100 text-14-400-18 color-black-olive">
//                           Acceptance successfully completed !!
//                         </div>
//                       ) : (
//                         <Formik
//                           initialValues={initialValues}
//                           validationSchema={validationSchema}
//                           onSubmit={handleSave}
//                           innerRef={formRef}
//                         >
//                           {(props) => {
//                             const { values } = props;
//                             return (
//                               <div className="row gy-4">
//                                 <div className="col-lg-6">
//                                   <AcceptanceTemplateForm
//                                     props={props}
//                                     initialValues={initialValues}
//                                     btnLoader={btnLoader}
//                                     paperData={paperData}
//                                     isEnable={isEnable}
//                                   />
//                                 </div>
//                                 <div className="col-lg-6 bg-f6ff overflow-auto">
//                                   <div className="cpt-21 cpe-21 cpb-21 cps-21">
//                                     <AcceptanceTemplatePreview
//                                       values={values}
//                                       templatePreviewRef={templatePreviewRef}
//                                       paperData={paperData}
//                                     />
//                                   </div>
//                                 </div>
//                               </div>
//                             );
//                           }}
//                         </Formik>
//                       ))}
//                     {tabType === '1' && (
//                       <div ref={paymentRef}>
//                         <RegistrationPayment
//                           paperData={paperData}
//                           handleSuccess={handleSuccess}
//                         />
//                       </div>
//                     )}
//                     {tabType === '2' && (
//                       <div ref={chatRef}>
//                         <ChatTab paperData={paperData} />
//                       </div>
//                     )}
//                   </>
//                 )
//               ) : (
//                 <>
//                   {!paymentIsAccepted && tabType === '2' && (
//                     <div ref={chatRef}>
//                       <div className="text-16-500 cpb-10 color-new-car">
//                         Chat
//                         {paperData?.unread_chat_count > 0 && (
//                           <span className="position-absolute unread-msg text-14-400">
//                             {paperData?.unread_chat_count || 0}
//                           </span>
//                         )}
//                       </div>
//                       <ChatTab paperData={paperData} />
//                     </div>
//                   )}
//                 </>
//               )}
//             </div>
//           ) : (
//             <>
//               {!paymentIsAccepted && tabType === '2' && (
//                 <div className="cpt-20" ref={chatRef}>
//                   <div className="text-16-500 cpb-10 color-new-car">
//                     Chat
//                     {paperData?.unread_chat_count > 0 && (
//                       <span className="position-absolute unread-msg text-14-400">
//                         {paperData?.unread_chat_count || 0}
//                       </span>
//                     )}
//                   </div>
//                   <ChatTab paperData={paperData} />
//                 </div>
//               )}
//             </>
//           )}
//         </div>
//       </div>
//     </>
//   );
// };

// export default AcceptanceState;
